import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Link } from 'react-router';
import { forceHttps } from 'helpers/ContentHelper';
import { onEnterKeyPress } from 'helpers/Account';
import { getCommonTealiumProps } from 'helpers/AccountHelper';
import {
  BUILD_VERSION,
  callTealiumEvent,
  getSegmentMVPDEvents,
  getTealiumSourceAuth,
  setSegmentPageStartSource,
  setTealiumProfileId,
  TEALIUM_EVENT_PREFIX,
  TealiumOnboardingActions,
} from '@foxcorp/delta-web-analytics';
import {
  SUCCESS_COOKIE_NAME,
  LOCKED_CONTENT_SOURCE,
  checkAuthStartSource,
  getAuthPlayerLinks,
  generateLinkTag,
} from 'helpers/AuthHelper';
import EmailSubmissionForm from 'components/EmailSubmissionForm';
import Style from './Providers.module.scss';

class Providers extends Component {
  constructor(props) {
    super(props);
    this.providerDialogRef = createRef();
  }

  componentDidMount() {
    const { topMVPDProviders } = this.props;
    const MVPDEvents = getSegmentMVPDEvents();
    if (!topMVPDProviders) {
      MVPDEvents.authenticationSelectProviderError({
        description: 'provider list failed to load',
        type: 'server-side',
      });
    }

    if (this.providerDialogRef) this.providerDialogRef.current?.focus();
  }

  render() {
    const {
      accessToken,
      account,
      deviceId,
      isLive,
      onAllProvidersClick,
      onProviderClick,
      previewPassExpired,
      previewPassSignIn,
      selectedChannel,
      showEmailForm,
      topMVPDProviders,
    } = this.props;

    if (!topMVPDProviders || !topMVPDProviders.length) return null;
    const {
      auth_selectProviderHeader: headerText,
      auth_selectProviderMessage: descriptionText,
      auth_seeAllProviders: seeAllProvidersText,
    } = window.Language;
    const profileId = _.get(account, 'profileId');
    const playerLinks = getAuthPlayerLinks();
    const { noProvider, needHelp } = playerLinks;
    const noProviderTag = generateLinkTag(Style, noProvider);
    const needHelpTag = generateLinkTag(Style, needHelp, () => {});
    const seeAllProvidersOnClick = () => {
      onAllProvidersClick();
    };

    const providerTags = () => {
      const isOptimizelyExperiment = window.optimizelyAllProvidersFocalPoint;
      const maxProviders = isOptimizelyExperiment ? 8 : 7;
      return topMVPDProviders.slice(0, maxProviders).map(provider => {
        const { name, logos, adobePassId } = provider;
        const logo = forceHttps(_.get(logos, '1.url', ''));

        const handleClick = () => {
          const id = adobePassId;
          const channel = isLive && selectedChannel ? selectedChannel : null;
          const cookie = {
            name: SUCCESS_COOKIE_NAME,
            value: {
              selectedChannel: channel,
              provider: id,
            },
            options: {
              path: '/',
            },
          };
          const action = 'authentication_select_provider';
          const authStartSource = getTealiumSourceAuth() || LOCKED_CONTENT_SOURCE;
          const onboardSrc = checkAuthStartSource(previewPassSignIn, previewPassExpired);
          const onboardingStartSource = onboardSrc;
          setSegmentPageStartSource({ previewPassSignIn, previewPassExpired });
          const data = setTealiumProfileId(profileId, {
            action,
            auth_start_source: authStartSource,
            dcg_profile_id: profileId,
            mvpd_select_type: 'primary mvpd list',
            mvpd: id,
            onboarding_action: TealiumOnboardingActions.COMPLETE,
            onboarding_start_source: onboardingStartSource,
            platform_version: BUILD_VERSION,
            profile_start_source: undefined,
            ...getCommonTealiumProps(),
          });
          onProviderClick(id, cookie);
          callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
        };
        return (
          <div className={Style.providerContainer} key={`provider-${name}`}>
            <Link
              className={Style.provider}
              tabIndex="0"
              title={name}
              onKeyPress={e => {
                onEnterKeyPress(e, handleClick);
              }}
              onClick={() => {
                handleClick();
              }}
            >
              <div className={Style.providerLogoContainer}>
                <img className={Style.providerLogo} src={logo} alt={name} />
              </div>
            </Link>
          </div>
        );
      });
    };

    const isOptimizelyExperiment = window.optimizelyAllProvidersFocalPoint;
    let allProvidersButton;
    let seeAllProviders = (
      <div className={Style.providerContainer} key="provider">
        <Link
          role="button"
          tabIndex="0"
          title={seeAllProvidersText}
          className={Style.provider}
          onClick={seeAllProvidersOnClick}
        >
          <span className={Style.allProviders}>{seeAllProvidersText}</span>
        </Link>
      </div>
    );
    if (isOptimizelyExperiment) {
      seeAllProviders = null;
      allProvidersButton = (
        <div className={Style.allProvidersButtonContainer}>
          <button
            type="button"
            onClick={seeAllProvidersOnClick}
            className={Style.allProvidersButton}
          >
            {seeAllProvidersText}
          </button>
        </div>
      );
    }

    return (
      <div
        className={Style.providerDialog}
        role="link"
        tabIndex="0"
        id="provider-dialog"
        ref={this.providerDialogRef}
      >
        <div key="header-text" className={Style.headerText}>
          {headerText}
        </div>
        <div key="description-text" className={Style.descriptionText}>
          {descriptionText}
        </div>
        <div key="providers-grid" className={Style.providersContainer}>
          {providerTags()}
          {seeAllProviders}
        </div>
        <EmailSubmissionForm
          accessToken={accessToken}
          account={account}
          deviceId={deviceId}
          key="email-form"
          render={showEmailForm}
          location="authentication flow"
        />
        {allProvidersButton}
        <div key="links" className={Style.links}>
          {noProviderTag}
          <span className={Style.linkSeparator} />
          {needHelpTag}
        </div>
      </div>
    );
  }
}

export default Providers;

Providers.defaultProps = {
  accessToken: null,
  account: {},
  deviceId: null,
  isLive: PropTypes.bool,
  onAllProvidersClick: () => {},
  onProviderClick: () => {},
  previewPassExpired: null,
  previewPassSignIn: false,
  selectedChannel: null,
  showEmailForm: false,
  topMVPDProviders: [],
};

Providers.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  deviceId: PropTypes.string,
  isLive: PropTypes.bool,
  onAllProvidersClick: PropTypes.func,
  onProviderClick: PropTypes.func,
  previewPassExpired: PropTypes.bool,
  previewPassSignIn: PropTypes.bool,
  selectedChannel: PropTypes.number,
  showEmailForm: PropTypes.bool,
  topMVPDProviders: PropTypes.arrayOf(PropTypes.shape({})),
};
